import { SCondition, RequestQueryBuilder } from '@nestjsx/crud-request';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { TorchAPIConfiguration } from '../../../../config/api.configuration';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Burly } from 'kb-burly';
import { ManyResponse } from '../../../../responses';
import {distinctUntilChanged, map} from 'rxjs/operators';
import { FleetPainByFleetAta } from '../../models';
import {Cacheable} from 'ts-cacheable';
import {FleetPainIndexTrends} from '../../models/fleet-pain-index-trends.model';

@Injectable({
  providedIn: 'root'
})
export class TorchFleetPainFleetAtaService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }


  @Cacheable()
  public queryFleetPainTable(painType: string = '1yr',
                             pageSize: number = 15,
                             currentPage: number = 0,
                             sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                             sortBy: string = 'rank',
                             filters: SCondition | null): Observable<ManyResponse<FleetPainByFleetAta>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection && sortDirection != null) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/')
      .addSegment(painType)
      .appendRawQueryString(qb.query(false));

    finalURL = endpoint.get;

    return this.httpClient.get<ManyResponse<FleetPainByFleetAta>>(finalURL);
  }

  @Cacheable()
  public getPainIndexMonths(painType: string = '1yr'): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/')
      .addSegment(painType)
      .addSegment('/painIndexMonth')
      .get;

    return this.httpClient.get<[{ pain_index_month: string }]>(endpoint).pipe(
      map(response => response.map(v => v.pain_index_month)),
      distinctUntilChanged()
    );
  }

  public getMaxPainIndexMonth(painType: string = '1yr'): Observable<string> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/')
      .addSegment(painType)
      .addSegment('/maxPainIndexMonth')
      .get;

    return this.httpClient.get<{ pain_index_month: string }>(endpoint).pipe(
      map(response => response.pain_index_month),
      distinctUntilChanged()
    );
  }

  @Cacheable()
  public getDistinctFleets(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/')
      .addSegment(painType)
      .addSegment('/fleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ fleet_cd: string }]>(endpoint).pipe(
      map(response => response.map(v => v.fleet_cd)),
      distinctUntilChanged()
    );
  }

  @Cacheable()
  public getDistinctAtas(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/')
      .addSegment(painType)
      .addSegment('/ata')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ata: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ata)),
      distinctUntilChanged()
    );
  }

  public getTop5AtasByFleet(fleet: string): Observable<FleetPainByFleetAta[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/1yr')
      .addSegment('/top5/fleet/' + fleet)
      .get;

    return this.httpClient.get<FleetPainByFleetAta[]>(endpoint).pipe(
      distinctUntilChanged()
    );
  }

  public exportToExcel(painType: string = '1yr', painData: FleetPainByFleetAta[], forBrief: boolean = false) {
    let segment: string;
    if (forBrief) {
      segment = '/exportBrief';
    } else {
      segment = '/export';
    }

    return new Observable(subscriber => {
      let endpoint = Burly(this.config.rootUrl)
        .addSegment('/pain-index/fleet/fleet-ata/')
        .addSegment(painType)
        .addSegment(segment)
        .get;

      const httpOptions: { headers; observe; responseType; } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        painData: painData
      }
      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public getFleetPainIndexTrends(fleet: string, ata: string): Observable<FleetPainIndexTrends[]> {

    let finalURL;

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/pain-index/fleet/fleet-ata/1yr')
      .addSegment('/trends/ata/' + ata + '/fleet/' + fleet);

    finalURL = endpointURL.get;

    return this.httpClient.get<FleetPainIndexTrends[]>(finalURL);
  }
}
